import type {
  DrawBoostReward,
  GameReward,
  Reward,
  RewardRedemptionCodeResponse,
  RewardRedemptionUrlResponse,
  VoucherReward,
} from '@/reward-types'
import {
  ReferrablBoxPrizeableType,
  type ReferralBoxPrizeableCredit,
  type ReferralBoxPrizeablePlan,
} from '@/services/referral-boxes'
import type {
  Campaign,
  CustomerPlayExclusion,
  CustomerSpendingLimit,
  Deposit,
  InstantWinDraw,
  InstantWinMainPrize,
  InstantWinPrize,
  PartnershipCampaign,
  Plan,
  Prize,
  PrizeCar,
  Product,
  User,
  Winner,
} from '@/types'

/**
 * Checks if the value is a valid User object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isUser = (value: any): value is User =>
  !!value?.__type && value.__type === 'User' && !!value.id

/**
 * Checks if the value is a valid Plan object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isPlan = (value: any): value is Plan =>
  !!value?.__type && value.__type === 'Plan'

/**
 * Checks if the value is a valid Prize object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isPrize = (value: any): value is Prize =>
  (!!value?.__type && value.__type === 'Prize') ||
  (!!value?.__type && value.__type === 'PrizeCar')

/**
 *Checks if the value is a valid PrizeCar object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isPrizeCar = (value: any): value is PrizeCar =>
  !!value?.__type && value.__type === 'PrizeCar'

/**
 * Checks if the value is a valid Winner object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isWinner = (value: any): value is Winner =>
  !!value?.__type && value.__type === 'Winner'

/**
 * Checks if the value is a valid InstantWinMainPrize object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isInstantWinMainPrize = (
  value: any
): value is InstantWinMainPrize =>
  !!value?.__type && value.__type === 'InstantWinMainPrize'

/**
 * Checks if the value is a valid InstantWinDraw object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isInstantWinDraw = (value: any): value is InstantWinDraw =>
  !!value?.__type && value.__type === 'InstantWinDraw'

/**
 * Checks if the value is a valid InstantWinPrize object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isInstantWinPrize = (value: any): value is InstantWinPrize =>
  !!value?.__type && value.__type === 'InstantWinPrize'

/**
 * Checks if the value is a valid Campaign object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isCampaign = (value: any): value is Campaign =>
  !!value?.__type && value.__type === 'Campaign'

/**
 * Checks if the value is a valid PartnershipCampaign object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isPartnershipCampaign = (
  value: any
): value is PartnershipCampaign =>
  !!value?.__type && value.__type === 'PartnershipCampaign'

/**
 * Checks if the value is a valid ReferralBoxPrize credit object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isReferralBoxPrizeCredit = (
  value: any
): value is ReferralBoxPrizeableCredit =>
  !!value?.referral_box_prizeable_type_id &&
  value.referral_box_prizeable_type_id === ReferrablBoxPrizeableType.Credit

/**
 * Checks if the value is a valid ReferralBoxPrize plan object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isReferralBoxPrizePlan = (
  value: any
): value is ReferralBoxPrizeablePlan =>
  !!value?.referral_box_prizeable_type_id &&
  value.referral_box_prizeable_type_id === ReferrablBoxPrizeableType.Plan

/**
 * Checks if the value is a valid Deposit object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isDeposit = (value: any): value is Deposit =>
  !!value?.__type && value.__type === 'Deposit'

/**
 * Checks if the value is a valid purchasable product.
 * @param {any} value
 * @returns {boolean}
 */
export const isPurchasableProduct = (value: any): value is Product =>
  isPlan(value) || isInstantWinDraw(value) || isDeposit(value)

/**
 * Checks if the reward is a VoucherReward.
 *
 * @param {Reward} value
 * @returns {value is VoucherReward}
 */
export const isVoucherReward = (value: Reward): value is VoucherReward =>
  value.redemption_type === 'voucher'

/**
 * Checks if the reward redemption response is a code redemption.
 *
 * @param {RewardRedemptionCodeResponse | RewardRedemptionUrlResponse} value
 * @returns {value is RewardRedemptionCodeResponse}
 */
export const isCodeRedemptionReward = (
  value: RewardRedemptionCodeResponse | RewardRedemptionUrlResponse
): value is RewardRedemptionCodeResponse => 'code' in value && !!value.code

/**
 * Checks if the reward redemption response is a URL redemption.
 *
 * @param {RewardRedemptionCodeResponse | RewardRedemptionUrlResponse} value
 * @returns {value is RewardRedemptionUrlResponse}
 */
export const isUrlRedemptionReward = (
  value: RewardRedemptionCodeResponse | RewardRedemptionUrlResponse
): value is RewardRedemptionUrlResponse => 'url' in value && !!value.url

/**
 * Checks if the reward is a DrawBoostReward.
 *
 * @param {Reward} value
 * @returns {value is DrawBoostReward}
 */
export const isDrawBoostReward = (value: Reward): value is DrawBoostReward =>
  value.redemption_type === 'draw_boost'

/**
 * Checks if the reward is a GameReward.
 *
 * @param {Reward} value
 * @returns {value is GameReward}
 */
export const isGameReward = (value: Reward): value is GameReward =>
  ['instant_win', 'daily_spin'].includes(value.redemption_type)

/**
 * Checks if the reward is a DailySpinReward.
 *
 * @param {Reward} value
 * @returns {value is GameReward}
 */
export const isDailySpinReward = (value: Reward): value is GameReward =>
  value.redemption_type === 'daily_spin'

/**
 * Checks if the value is a valid CustomerSpendingLimit object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isCustomerSpendingLimit = (
  value: any
): value is CustomerSpendingLimit =>
  !!value?.__type && value.__type === 'CustomerSpendingLimit'

/**
 * Checks if the value is a valid CustomerPlayExclusion object.
 *
 * @param {any} value
 * @returns {boolean}
 */
export const isCustomerPlayExclusion = (
  value: any
): value is CustomerPlayExclusion =>
  !!value?.__type && value.__type === 'CustomerPlayExclusion'
